import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Platform } from '@ionic/angular';
import { Subscription } from 'rxjs';

/** Longest a task name may get before it is cut. */
const MAX_NAME_LENGTH = 80;

/** One line of the sheet. `delay` is the start of its fill, in the SCSS timeline (`$t-lines` + i·0.2s). */
interface SheetLine {
  y: number;
  w: number;
  delay: string;
}

/** One speed streak behind the plane. */
interface PlaneStreak {
  x: number;
  y: number;
  w: number;
  strong: boolean;
  delay: string;
}

/** Same geometry as the reference ("Tarea con animación", state «Enviada»), in the SVG's 200×180 box. */
const LINES: SheetLine[] = [0, 1, 2, 3, 4].map((i) => ({
  y: 62 + i * 16,
  w: i === 4 ? 44 : i === 0 ? 50 : 72,
  delay: `${0.55 + i * 0.2}s`,
}));

const STREAKS: PlaneStreak[] = [
  [10, 80, 34, 0],
  [2, 96, 40, 0.28],
  [16, 112, 24, 0.14],
  [24, 64, 18, 0.42],
].map(([x, y, w, d], i) => ({ x, y, w, strong: i === 1, delay: `${2 + d}s` }));

/**
 * The "your task was sent" moment: a sheet fills its lines, folds away and turns into a paper plane,
 * then the card's text comes in. Port of the «Enviada» state of the design team's reference, in the
 * brand's primary instead of its fixed colours.
 *
 * Reusable and knows nothing about courses, same contract as `app-survey-sent-celebration`: mount it
 * with an `*ngIf` and it plays from its creation; it tells the host when it is closed
 * ({@link dismissed}) and the host removes it. It can be closed at any time — the button, the X,
 * Escape, a tap on the dimmed area or the hardware back button.
 *
 * The whole timeline lives in the SCSS. `prefers-reduced-motion` skips it and shows the final card.
 */
@Component({
  selector: 'app-task-sent-modal',
  templateUrl: './task-sent-modal.component.html',
  styleUrls: ['./task-sent-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskSentModalComponent implements OnInit, OnDestroy {
  /** Name of the task that was sent, printed under the title. */
  @Input()
  set taskName(value: string) {
    const name = (value ?? '').trim();
    this.name = name.length > MAX_NAME_LENGTH ? `${name.slice(0, MAX_NAME_LENGTH - 1).trimEnd()}…` : name;
  }

  /** The learner closed it. The host clears whatever mounted this component. */
  @Output() dismissed = new EventEmitter<void>();

  name = '';
  readonly reduced = prefersReducedMotion();
  readonly lines = LINES;
  readonly streaks = STREAKS;

  private closed = false;
  private backButton?: Subscription;

  constructor(private platform: Platform) {}

  ngOnInit(): void {
    // Above Ionic's own overlays (100): while this is open, "back" closes it instead of leaving the page.
    this.backButton = this.platform.backButton.subscribeWithPriority(101, () => this.close());
  }

  ngOnDestroy(): void {
    this.backButton?.unsubscribe();
  }

  @HostListener('document:keydown.escape')
  close(): void {
    // Guarded: Escape and the back button can both arrive for the same press.
    if (this.closed) return;
    this.closed = true;
    this.dismissed.emit();
  }
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
}
